import { useQuery } from '@tanstack/react-query';
import useAuth from '../../auth/feature/auth';
import { useProductAccess } from '../../common/hooks/use-product-access';
import { riserService, RiserPage } from '../service/riser.service';

/**
 * Slug of the admin-granted Riser entitlement. Must match what the admin panel
 * writes into product_access_grants and what the API checks for.
 */
export const RISER_PRODUCT_SLUG = 'riser';

export type RiserAccess = {
  /** True only for a signed-in account that holds the grant. */
  granted: boolean;
  /** True while we still do not know, so nothing has to be guessed at. */
  isLoading: boolean;
  /** The page, or null. Null covers every reason, on purpose. */
  page: RiserPage | null;
};

/**
 * Whether this account can see Riser, and the page if it can.
 *
 * Two layers, and they are not redundant. The grant list decides whether to
 * ASK, which is why a visitor with no grant never fires a request that would
 * 404 and never sees a failed call in their network tab. The API decides what
 * to ANSWER, which is the actual boundary: the copy, the nav entry and the
 * version all arrive from it or not at all. Deleting the check here would cost
 * a wasted request and change nothing about who can read the page.
 *
 * Shares the ['riser-page'] key, so the header menu and the page itself fetch
 * it once between them. The key is invalidated by the realtime access channel,
 * so an admin granting access opens the product in place rather than on the
 * next reload.
 */
export const useRiserPage = (): RiserAccess => {
  const { isAuthenticated } = useAuth();
  const access = useProductAccess();
  const granted = !!isAuthenticated && access.has(RISER_PRODUCT_SLUG);

  const query = useQuery({
    queryKey: ['riser-page'],
    queryFn: async () => riserService.getPage(),
    enabled: granted,
    retry: false
  });

  return {
    granted,
    isLoading: access.isLoading || (granted && query.isLoading),
    page: (granted && query.data) || null
  };
};
